import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { Product } from '../../types/product';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import EmailVerificationModal from '../modals/EmailVerificationModal';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { user } = useAuth();
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleBuyNow = async () => {
    if (product.external_url) {
      // If product is free and user is not logged in
      if (product.price === 0 && !user) {
        const storedEmail = localStorage.getItem('blooma_email');
        
        if (storedEmail) {
          try {
            // Check if user exists in our database
            const { data: existingUser } = await supabase
              .from('auth.users')
              .select('*')
              .eq('email', storedEmail.toLowerCase())
              .maybeSingle();

            // If user exists, allow download regardless of authentication status
            if (existingUser) {
              window.location.href = product.external_url;
              return;
            }
          } catch (err) {
            console.error('Error checking user:', err);
            // Continue to email modal if there's an error
          }
        }
        
        // Show email modal if no stored email or user doesn't exist
        setShowEmailModal(true);
        return;
      }

      // Open download directly for logged in users or paid products
      window.location.href = product.external_url;
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
        <div className="aspect-w-16 aspect-h-9">
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-48 object-cover"
          />
        </div>
        <div className="p-4">
          <div className="mb-2">
            {product.category && (
              <span className="text-sm text-primary-600 font-medium">
                {product.category.name}
              </span>
            )}
          </div>
          <Link to={`/shop/${product.slug}`}>
            <h3 className="font-semibold text-lg hover:text-primary-600 transition-colors">
              {product.name}
            </h3>
          </Link>
          <p className="text-gray-600 text-sm mt-2 line-clamp-2">{product.description}</p>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-lg font-bold text-primary-600">
              {product.price === 0 ? 'Free' : `$${product.price.toFixed(2)}`}
            </span>
            {product.external_url && (
              <button
                onClick={handleBuyNow}
                className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors text-sm"
              >
                <span>{product.price === 0 ? 'Download Now' : 'Buy Now'}</span>
                <ExternalLink className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <EmailVerificationModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onVerified={() => {
          setShowEmailModal(false);
          if (product.external_url) {
            window.location.href = product.external_url;
          }
        }}
      />
    </>
  );
}